'use client';

import React, { useState, useEffect, useRef } from 'react';
import { colors, baseStyles, formatUSD, formatNum, formatSmartPrice, LiveCompareBadge } from './SharedUI';
import SupportResistanceRadar from './SupportResistanceRadar';
import SupportResistanceRadarV2 from './SupportResistanceRadarV2';
import SupportResistanceRadarV3 from './SupportResistanceRadarV3';
import PatternMatrixXRay from './PatternMatrixXRay';
import LiquidationSqueezeRadar from './LiquidationSqueezeRadar';
import RealtimeDumpRadar from './RealtimeDumpRadar'; 
import LeverageSimulator from './LeverageSimulator'; 
import AICouncilSection from './AICouncilSection'; // [MỚI] Import Hội đồng AI
import MultiTimeframeMatrix from './MultiTimeframeMatrix'; // [MỚI]
import TimeBoxedOrderFlow from './TimeBoxedOrderFlow';
import FundingRateHistory from './FundingRateHistory';
import DiffOrderBook from './DiffOrderBook';
import USStockOHLCMatrix from './USStockOHLCMatrix';
import USStockVisualAnalytics from './USStockVisualAnalytics';
import AdvancedOrderflowMatrix from './AdvancedOrderflowMatrix';


export default function HistoricalSection({ symbol, liveData, smartMoneyRef, liveDataRef }) { 
  const options = [
    { id: '15m', label: '15p trước', limit: 2, interval: '15m', oiPeriod: '15m' },
    { id: '30m', label: '30p trước', limit: 2, interval: '30m', oiPeriod: '30m' },
    { id: '1h', label: '1h trước', limit: 2, interval: '1h', oiPeriod: '1h' },
    { id: '2h', label: '2h trước', limit: 2, interval: '2h', oiPeriod: '2h' },
    { id: '4h', label: '4h trước', limit: 2, interval: '4h', oiPeriod: '4h' },
    { id: '12h', label: '12h trước', limit: 2, interval: '12h', oiPeriod: '12h' },
    { id: '1d', label: 'Hôm qua', limit: 2, interval: '1d', oiPeriod: '1d' },
    { id: '2d', label: '2 Ngày trước', limit: 3, interval: '1d', oiPeriod: '1d' }
  ];

  const [selected, setSelected] = useState(['1d']);
  const [histDataList, setHistDataList] = useState([]);
  const [spotDataList, setSpotDataList] = useState([]);
  
  const [spotList, setSpotList] = useState([]);
  const [alphaList, setAlphaList] = useState([]);

  const [loadingFutures, setLoadingFutures] = useState(false);
  const [loadingSpot, setLoadingSpot] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  const [activeSpotSymbol, setActiveSpotSymbol] = useState(null);
  const [activeMarketType, setActiveMarketType] = useState(""); 
  
  const [manipulationReport, setManipulationReport] = useState(null);
  const [sharedZones, setSharedZones] = useState({ res: null, sup: null, isScanned: false });
  
  // Cây Cầu (State) nối giữa V3 và Radar
  const [sharedZonesV3, setSharedZonesV3] = useState(null);

  // ==========================================
  // [MỚI] KHAI BÁO REFS ĐỂ HÚT DATA CHO AI
  // ==========================================
  const v3Ref = useRef(null);
  const radarRef = useRef(null);
  const simulatorRef = useRef(null);

  const toggleOption = (id) => setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  useEffect(() => {
    const fetchMarketLists = async () => {
      try {
        const spotRes = await fetch('https://api.binance.com/api/v3/exchangeInfo');
        const spotData = await spotRes.json();
        const validSpot = spotData.symbols.filter(s => s.status === 'TRADING').map(s => s.symbol);
        setSpotList(validSpot);

        const alphaUrl = 'https://www.binance.com/bapi/defi/v1/public/wallet-direct/buw/wallet/cex/alpha/all/token/list';
        const alphaRes = await fetch(alphaUrl);
        const alphaJson = await alphaRes.json();
        
        if (alphaJson && alphaJson.data) {
          const list = alphaJson.data.map(item => ({
             symbol: item.symbol.toUpperCase(), 
             alphaId: item.alphaId              
          }));
          setAlphaList(list);
        }
      } catch (error) {
        console.error("❌ Lỗi khi load danh sách Spot/Alpha:", error);
      }
    };
    fetchMarketLists();
  }, []);

  const fetchHistoryData = async () => {
    if (selected.length === 0) return;
    setLoadingFutures(true);
    setManipulationReport(null); 
    try {
      let fundData = [];
      try {
        const fundRes = await fetch(`https://fapi.binance.com/fapi/v1/fundingRate?symbol=${symbol}&limit=50`);
        fundData = await fundRes.json();
      } catch (e) {}

      const results = await Promise.all(selected.map(async (id) => {
        const opt = options.find(o => o.id === id);
        const targetIndex = 0; 
        const currentIndex = opt.limit - 1; 

        const klineRes = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=${opt.interval}&limit=${opt.limit}`);
        const klineData = await klineRes.json();
        
        const k = klineData[targetIndex]; 
        const kLive = klineData[currentIndex]; 

        let oiContract = null;
        try {
          const oiRes = await fetch(`https://fapi.binance.com/futures/data/openInterestHist?symbol=${symbol}&period=${opt.oiPeriod}&limit=${opt.limit}`);
          if (oiRes.ok) {
            const oiData = await oiRes.json();
            if (oiData && oiData.length > targetIndex) oiContract = parseFloat(oiData[targetIndex].sumOpenInterest);
          }
        } catch (e) {}

        const vol = parseFloat(k[7]);
        const takerBuy = parseFloat(k[10]);
        const cvd = takerBuy - (vol - takerBuy);

        const liveVol = parseFloat(kLive[7]);
        const liveTakerBuy = parseFloat(kLive[10]);
        const liveCvd = liveTakerBuy - (liveVol - liveTakerBuy);
        const volProgress = vol > 0 ? ((liveVol / vol) * 100).toFixed(1) : 0;

        const kCloseTime = k[6];
        const closestFund = fundData.slice().reverse().find(f => f.fundingTime <= kCloseTime);
        const histFundingRate = closestFund ? parseFloat(closestFund.fundingRate) : null;

        return {
          id: opt.id, label: opt.label,
          open: parseFloat(k[1]), high: parseFloat(k[2]), low: parseFloat(k[3]), close: parseFloat(k[4]),
          volume: vol, cvd: cvd, oiContract: oiContract || "N/A", histFundingRate,
          liveVol: liveVol, liveCvd: liveCvd, volProgress: parseFloat(volProgress)
        };
      }));
      results.sort((a, b) => options.findIndex(o => o.id === a.id) - options.findIndex(o => o.id === b.id));
      setHistDataList(results);
    } catch (error) { console.error(error); } finally { setLoadingFutures(false); }
  };

  const fetchSpotHistoryData = async () => {
    if (selected.length === 0) return;
    setLoadingSpot(true);
    setManipulationReport(null);

    try {
      const cleanSymbol = symbol.replace(/^(1000000|10000|1000|100)/, '');
      let targetSymbol = cleanSymbol; 
      let marketType = 'UNKNOWN';
      let apiFetchId = cleanSymbol;   

      if (spotList.includes(cleanSymbol)) {
        marketType = 'SPOT';
        apiFetchId = cleanSymbol; 
        targetSymbol = cleanSymbol;
      } 
      else if (spotList.includes(symbol)) {
        marketType = 'SPOT';
        apiFetchId = symbol;
        targetSymbol = symbol;
      } 
      else {
        const quoteMatch = cleanSymbol.match(/(USDT|USDC)$/i);
        const quote = quoteMatch ? quoteMatch[0].toUpperCase() : 'USDT'; 
        const tokenOnly = cleanSymbol.replace(/(USDT|USDC)$/i, '');
        
        const alphaMatch = alphaList.find(a => {
          const aSym = typeof a === 'string' ? a : (a.symbol || a.asset || '');
          return aSym.toUpperCase() === tokenOnly.toUpperCase();
        });
        
        if (alphaMatch) {
          marketType = 'ALPHA';
          apiFetchId = `${alphaMatch.alphaId}${quote}`; 
          targetSymbol = tokenOnly.toUpperCase();
        }
      }

      if (marketType === 'UNKNOWN') {
        alert(`❌ Không tìm thấy mã Giao ngay cho ${symbol}. Dò Spot (${cleanSymbol}) và Alpha đều thất bại!`);
        setLoadingSpot(false);
        return;
      }

      setActiveSpotSymbol(targetSymbol);
      setActiveMarketType(marketType);

      const results = await Promise.all(selected.map(async (id) => {
        const opt = options.find(o => o.id === id);
        const targetIndex = 0; 
        const currentIndex = opt.limit - 1; 

        let apiUrl = '';
        if (marketType === 'SPOT') {
          apiUrl = `https://api.binance.com/api/v3/klines?symbol=${apiFetchId}&interval=${opt.interval}&limit=${opt.limit}`;
        } else if (marketType === 'ALPHA') {
          apiUrl = `https://www.binance.com/bapi/defi/v1/public/alpha-trade/klines?symbol=${apiFetchId}&interval=${opt.interval}&limit=${opt.limit}`;
        }

        const klineRes = await fetch(apiUrl);
        if (!klineRes.ok) throw new Error("Data not found");
        
        const rawJson = await klineRes.json();
        const klineData = rawJson.data ? rawJson.data : rawJson; 
        
        const k = klineData[targetIndex]; 
        const kLive = klineData[currentIndex]; 

        const vol = parseFloat(k[7]); 
        const takerBuy = parseFloat(k[10]); 
        const cvd = takerBuy - (vol - takerBuy);

        const liveVol = parseFloat(kLive[7]);
        const liveTakerBuy = parseFloat(kLive[10]);
        const liveCvd = liveTakerBuy - (liveVol - liveTakerBuy);
        const volProgress = vol > 0 ? ((liveVol / vol) * 100).toFixed(1) : 0;

        return {
          id: opt.id, label: opt.label,
          open: parseFloat(k[1]), high: parseFloat(k[2]), low: parseFloat(k[3]), close: parseFloat(k[4]),
          volume: vol, cvd: cvd,
          liveVol: liveVol, liveCvd: liveCvd, volProgress: parseFloat(volProgress),
          liveClose: parseFloat(kLive[4]) 
        };
      }));

      results.sort((a, b) => options.findIndex(o => o.id === a.id) - options.findIndex(o => o.id === b.id));
      setSpotDataList(results);
    } catch (error) { 
      console.error("Lỗi lấy dữ liệu Hàng thật:", error); 
      setSpotDataList([]);
      alert(`Đã có lỗi khi kéo dữ liệu ${activeMarketType} cho mã ${symbol}. Vui lòng thử lại!`);
    } finally { 
      setLoadingSpot(false); 
    }
  };

  const analyzeManipulation = () => {
    if (histDataList.length === 0 || spotDataList.length === 0) {
      alert("Vui lòng Fetch đầy đủ dữ liệu Futures và Spot trước khi phân tích!");
      return;
    }

    setAnalyzing(true);
    
    const sortedSelected = [...selected].sort((a, b) => options.findIndex(o => o.id === a) - options.findIndex(o => o.id === b));
    
    const microResults = sortedSelected.map(id => {
      const fData = histDataList.find(d => d.id === id);
      const sData = spotDataList.find(d => d.id === id);
      const opt = options.find(o => o.id === id);

      if (!fData || !sData || !liveData) return null;

      const fChangePct = ((liveData.last - fData.open) / fData.open) * 100;
      const sChangePct = ((sData.liveClose - sData.open) / sData.open) * 100;
      const spotVolRatio = (sData.liveVol / (fData.liveVol + sData.liveVol)) * 100;
      const premiumSpread = liveData.last - sData.liveClose; 
      const isOiRising = fData.oiContract !== "N/A" && liveData.oiContract && liveData.oiContract > fData.oiContract;

      let type = 'NORMAL';
      let title = "Bình thường";
      let color = colors.textSub;

      if (fChangePct > 3 && isOiRising && spotVolRatio < 20 && premiumSpread > 0) {
        type = 'A_LONG'; 
        title = `🚨 Bong Bóng Fomo (Rủi ro LONG SQUEEZE - Diệt Long 🔴)`; 
        color = colors.red;
      }
      else if (fChangePct < -3 && isOiRising && spotVolRatio < 20 && premiumSpread < 0) {
        type = 'A_SHORT'; 
        title = `🚨 Bong Bóng Bán Khống (Rủi ro SHORT SQUEEZE - Diệt Short 🟢)`; 
        color = colors.green;
      }
      else if (fChangePct <= 0 && isOiRising && fData.histFundingRate < 0 && spotVolRatio > 35) {
        type = 'B'; 
        title = `🐳 Gom Hàng Chờ Bơm (Rủi ro SHORT SQUEEZE 🟢)`; 
        color = colors.green;
      }
      else if (premiumSpread < 0 && fChangePct < -3) {
        type = 'C'; 
        title = `🔥 Chiết Khấu Hoảng Loạn (Canh Bắt Đáy 🟢)`; 
        color = colors.green;
      }
      else if (fChangePct > 0 && fData.liveCvd > 0 && sData.liveCvd < 0 && spotVolRatio > 30) {
        type = 'D'; 
        title = `⚠️ Phân Phối Kéo Xả (Rủi ro SẬP HẦM 🔴)`; 
        color = colors.warn;
      }

      return {
        id, label: opt.label, type, title, color,
        fChangePct, sChangePct, spotVolRatio, premiumSpread,
        futPrice: liveData.last, spotPrice: sData.liveClose,
        futVol: fData.liveVol, spotVol: sData.liveVol
      };
    }).filter(Boolean);

    if (microResults.length === 0) {
      setAnalyzing(false); return;
    }

    let macroTitle = "";
    let macroDesc = "";
    let macroColor = colors.textMain;

    const shortestType = microResults[0].type;
    const longestType = microResults[microResults.length - 1].type;
    
    const isAllSame = microResults.every(r => r.type === shortestType);

    if (isAllSame) {
      if (shortestType === 'NORMAL') {
        macroTitle = "✅ THỊ TRƯỜNG CÂN BẰNG (ALL CLEAR)";
        macroColor = colors.green;
        macroDesc = "Dòng tiền Spot và Futures đồng thuận trên tất cả các khung thời gian. Không phát hiện dấu hiệu lệch pha cấu trúc.";
      } else {
        macroTitle = `🔥 ĐỒNG THUẬN TUYỆT ĐỐI (STRONG CONFLUENCE)`;
        macroColor = microResults[0].color;
        macroDesc = `Hành vi thao túng đang diễn ra có hệ thống trên mọi khung thời gian soi chiếu. Quyết tâm của Cá mập là rất rõ ràng!`;
      }
    } else {
      if (longestType !== 'NORMAL' && shortestType === 'NORMAL') {
        macroTitle = "⚖️ TRỞ LẠI CÂN BẰNG (FADING MANIPULATION)";
        macroColor = colors.textMain;
        macroDesc = `Áp lực bất thường ở khung dài hạn đã bắt đầu chững lại. Dòng tiền trong ngắn hạn đang trở về mức ổn định tự nhiên.`;
      } 
      else if (longestType === 'NORMAL' && shortestType !== 'NORMAL') {
        macroTitle = "⚠️ ĐẢO PHA NGẮN HẠN (SHORT-TERM SHIFT)";
        macroColor = microResults[0].color;
        macroDesc = `Cấu trúc dài hạn vốn đang bình thường nhưng Market Maker vừa có động thái "quay xe" chớp nhoáng! Cẩn thận một cú rũ hàng (Shake-out).`;
      } 
      else {
        const isBearish = (t) => t === 'A_LONG' || t === 'D'; 
        const isBullish = (t) => t === 'A_SHORT' || t === 'B' || t === 'C'; 
        
        const isConflict = (isBearish(shortestType) && isBullish(longestType)) || (isBullish(shortestType) && isBearish(longestType));
        
        if (isConflict) {
          macroTitle = "🌪️ NHIỄU LOẠN HỖN MANG (CONFLICTING SIGNALS)";
          macroColor = colors.warn;
          macroDesc = `Khung ngắn và dài đang mâu thuẫn hoàn toàn (Phe Bò và Gấu đang đánh nhau tranh giành thanh khoản). Khuyến nghị đứng ngoài quan sát!`;
        } else {
          macroTitle = "⚠️ CHUYỂN BIẾN PHỨC TẠP (MIXED SIGNALS)";
          macroColor = colors.warn;
          macroDesc = `Thị trường đang có sự chuyển dịch hành vi giữa các khung thời gian. Vui lòng đối chiếu chi tiết timeline bên dưới để dò tìm điểm nghẽn dòng tiền.`;
        }
      }
    }

    setManipulationReport({ 
      macro: { title: macroTitle, desc: macroDesc, color: macroColor },
      micro: microResults 
    });
    
    setAnalyzing(false);
  };

  useEffect(() => { fetchHistoryData(); }, [symbol]);

  // ==========================================
  // [MỚI] HÀM GOM DATA ĐỂ TRUYỀN CHO AI COUNCIL
  // ==========================================
  const buildSnapshotData = () => {
    // Kéo data ngầm từ 3 Component qua Ref
    const v3Data = v3Ref.current?.getSnapshot() || {};
    const radarData = radarRef.current?.getSnapshot() || {};
    const simData = simulatorRef.current?.getSnapshot() || {};

    const livePrice = v3Data.liveScan?.livePrice || liveData?.last || 0;

    return {
      timestamp: new Date().toLocaleTimeString('vi-VN'),
      macro: {
        price: livePrice,
        positionStr: v3Data.liveScan?.positionStr || 'Chưa rõ',
        sellZone: `[ $${formatNum(v3Data.macroData?.zones?.sell?.lower || 0)} - $${formatNum(v3Data.macroData?.zones?.sell?.upper || 0)} ]`,
        buyZone: `[ $${formatNum(v3Data.macroData?.zones?.buy?.lower || 0)} - $${formatNum(v3Data.macroData?.zones?.buy?.upper || 0)} ]`,
      },
      micro: {
        imbalance: v3Data.microMetrics?.imbalance || 0,
        cvd: v3Data.microMetrics?.cvd || 0,
        fakeAsks: radarData.towMetrics?.asksFake || 0,
        realAsks: radarData.towMetrics?.asksReal || 0,
        fakeBids: radarData.towMetrics?.bidsFake || 0,
        realBids: radarData.towMetrics?.bidsReal || 0,
      },
      smartMoney: {
        longAvg: smartMoneyRef?.current?.longWhalesAvgEntryPrice || 0,
        shortAvg: smartMoneyRef?.current?.shortWhalesAvgEntryPrice || 0,
      },
      wallet: {
        margin: simData.margin || 1000,
        leverage: simData.leverage || 10,
      }
    };
  };

  return (
    <div style={{ ...baseStyles.card, marginTop: '24px' }}>
      
      {/* ======================= HEADER CONTROL ======================= */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <strong style={{ color: colors.textMain }}>⏱️ MULTI-TIMEFRAME & RADAR:</strong>
          {options.map(opt => (
            <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px', color: selected.includes(opt.id) ? colors.green : colors.textSub }}>
              <input type="checkbox" checked={selected.includes(opt.id)} onChange={() => toggleOption(opt.id)} />
              {opt.label}
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={fetchHistoryData} disabled={loadingFutures} style={{ backgroundColor: colors.bg, color: colors.textMain, border: `1px solid ${colors.border}`, padding: '6px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
            {loadingFutures ? '⌛...' : '📥 FETCH FUTURES'}
          </button>
          <button onClick={fetchSpotHistoryData} disabled={loadingSpot} style={{ backgroundColor: colors.greenBg, color: colors.green, border: `1px solid ${colors.green}`, padding: '6px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
            {loadingSpot ? '⌛...' : '📥 FETCH SPOT (HÀNG THẬT)'}
          </button>
        </div>
      </div>

      {/* ======================= BẢNG DATA ======================= */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* 1. KHỐI FUTURES */}
        <div>
           <div style={{fontSize: '13px', fontWeight: 'bold', color: '#3b82f6', marginBottom: '8px'}}>🟦 THỊ TRƯỜNG PHÁI SINH (FUTURES)</div>
           <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {histDataList.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', backgroundColor: 'rgba(59, 130, 246, 0.05)', padding: '10px 12px', borderRadius: '6px', fontSize: '12px', borderLeft: `3px solid #3b82f6` }}>
                <div style={{ minWidth: '130px', fontWeight: 'bold', color: colors.textMain }}>[ {item.label.toUpperCase()} ]</div>
                <div style={{ display: 'flex', gap: '12px', flex: 1, borderRight: `1px solid ${colors.border}`, paddingRight: '12px' }}>
                  <span><span style={{color: colors.textSub}}>O:</span> ${formatSmartPrice(item.open)}</span>
                  <span><span style={{color: colors.textSub}}>H:</span> ${formatSmartPrice(item.high)} <LiveCompareBadge liveVal={liveData?.last} histVal={item.high} type="breakout"/></span>
                  <span><span style={{color: colors.textSub}}>L:</span> ${formatSmartPrice(item.low)}</span>
                  <span><span style={{color: colors.textSub}}>C:</span> ${formatSmartPrice(item.close)} <LiveCompareBadge liveVal={liveData?.last} histVal={item.open} type="percent"/></span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '220px' }}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <span><span style={{color: colors.textSub}}>OI:</span> {item.oiContract !== "N/A" ? formatNum(item.oiContract) : "N/A"} <LiveCompareBadge liveVal={liveData?.oiContract} histVal={item.oiContract} type="percent"/></span>
                    <span><span style={{color: colors.textSub}}>VOL:</span> {formatUSD(item.volume)} <span style={{ color: item.volProgress >= 100 ? colors.green : colors.textSub, fontSize: '11px', marginLeft: '4px', fontWeight: item.volProgress >= 100 ? 'bold' : 'normal' }}>(Đạt: {item.volProgress}%)</span></span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <span><span style={{color: colors.textSub}}>CVD:</span> {formatUSD(item.cvd)} <span style={{ color: item.liveCvd > 0 ? colors.green : colors.red, fontSize: '11px', marginLeft: '4px' }}>(H.tại: {item.liveCvd > 0 ? '+' : ''}{formatUSD(item.liveCvd)})</span></span>
                    <span><span style={{color: colors.textSub}}>FUNDING:</span> <strong style={{color: item.histFundingRate > 0 ? colors.green : (item.histFundingRate < 0 ? colors.red : colors.textMain)}}>{item.histFundingRate !== null ? `${item.histFundingRate > 0 ? '+' : ''}${(item.histFundingRate * 100).toFixed(4)}%` : 'N/A'}</strong></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 2. KHỐI SPOT */}
        {spotDataList.length > 0 && (
          <div>
            <div style={{fontSize: '13px', fontWeight: 'bold', color: colors.green, marginBottom: '8px'}}>🟩 THỊ TRƯỜNG GIAO NGAY ({activeMarketType}) - {activeSpotSymbol}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {spotDataList.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', backgroundColor: 'rgba(34, 197, 94, 0.05)', padding: '10px 12px', borderRadius: '6px', fontSize: '12px', borderLeft: `3px solid ${colors.green}` }}>
                  <div style={{ minWidth: '130px', fontWeight: 'bold', color: colors.textMain }}>[ {item.label.toUpperCase()} ]</div>
                  <div style={{ display: 'flex', gap: '12px', flex: 1, borderRight: `1px solid ${colors.border}`, paddingRight: '12px' }}>
                    <span><span style={{color: colors.textSub}}>O:</span> ${formatSmartPrice(item.open)}</span>
                    <span><span style={{color: colors.textSub}}>H:</span> ${formatSmartPrice(item.high)} <LiveCompareBadge liveVal={item.liveClose} histVal={item.high} type="breakout"/></span>
                    <span><span style={{color: colors.textSub}}>L:</span> ${formatSmartPrice(item.low)}</span>
                    <span><span style={{color: colors.textSub}}>C:</span> ${formatSmartPrice(item.close)} <LiveCompareBadge liveVal={item.liveClose} histVal={item.open} type="percent"/></span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '220px' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <span><span style={{color: colors.textSub}}>VOL THẬT:</span> {formatUSD(item.volume)} </span>
                      <span><span style={{ color: item.volProgress >= 100 ? colors.green : colors.textSub, fontSize: '11px', fontWeight: item.volProgress >= 100 ? 'bold' : 'normal' }}>(Đạt: {item.volProgress}%)</span></span>
                    </div>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <span><span style={{color: colors.textSub}}>CVD SPOT:</span> {formatUSD(item.cvd)} <span style={{ color: item.liveCvd > 0 ? colors.green : colors.red, fontSize: '11px', marginLeft: '4px' }}>(H.tại: {item.liveCvd > 0 ? '+' : ''}{formatUSD(item.liveCvd)})</span></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ======================= BÁO CÁO MACRO & MICRO ======================= */}
      {spotDataList.length > 0 && histDataList.length > 0 && (
        <div style={{ marginTop: '24px', borderTop: `1px dashed ${colors.border}`, paddingTop: '16px', textAlign: 'center' }}>
          <button 
            onClick={analyzeManipulation} 
            style={{ backgroundColor: 'transparent', border: `2px solid ${colors.warn}`, color: colors.warn, padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}
          >
            {analyzing ? '⚖️ ĐANG TỔNG HỢP...' : '⚖️ QUÉT SỰ HỢP LƯU (CONFLUENCE RADAR)'}
          </button>

          {manipulationReport && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: colors.bg, border: `1px solid ${manipulationReport.macro.color}`, borderRadius: '8px', textAlign: 'left' }}>
              
              <div style={{ fontWeight: 'bold', fontSize: '18px', color: manipulationReport.macro.color, marginBottom: '8px' }}>
                {manipulationReport.macro.title}
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.5', color: colors.textMain, marginBottom: '24px', paddingBottom: '20px', borderBottom: `1px dashed ${colors.border}` }}>
                {manipulationReport.macro.desc}
              </div>

              <div style={{ fontWeight: 'bold', fontSize: '13px', color: colors.textSub, marginBottom: '12px' }}>
                ⏱️ CHI TIẾT DÒNG THỜI GIAN (TỪ NGẮN ĐẾN DÀI HẠN):
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {manipulationReport.micro.map(res => (
                  <div key={res.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', padding: '10px 16px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '6px', borderLeft: `4px solid ${res.color}` }}>
                    <div style={{ minWidth: '100px', fontWeight: 'bold', fontSize: '13px', color: colors.textMain }}>[ {res.label.toUpperCase()} ]</div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', color: res.color, fontSize: '14px', marginBottom: '6px' }}>
                        {res.title}
                      </div>
                      
                      <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span>Tỷ trọng Spot: <strong style={{color: colors.textMain}}>{res.spotVolRatio.toFixed(1)}%</strong> <span style={{fontSize: '11px'}}>(Spot: <span style={{color: colors.green}}>{formatUSD(res.spotVol)}</span> / Fut: <span style={{color: '#3b82f6'}}>{formatUSD(res.futVol)}</span>)</span></span>
                        <span>Độ lệch giá (Premium): <strong style={{color: colors.textMain}}>${res.premiumSpread.toFixed(4)}</strong> <span style={{fontSize: '11px'}}>(Fut: <span style={{color: '#3b82f6'}}>${formatSmartPrice(res.futPrice)}</span> - Spot: <span style={{color: colors.green}}>${formatSmartPrice(res.spotPrice)}</span>)</span></span>
                        <span>Biến động (Fut/Spot): <strong style={{color: colors.textMain}}>{res.fChangePct.toFixed(2)}% / {res.sChangePct.toFixed(2)}%</strong></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================= COMPONENT CON (GẮN REFS) ======================= */}
      <SupportResistanceRadar 
        symbol={symbol} 
        liveData={liveData} 
        histDataList={histDataList} 
        onScanComplete={(zones) => setSharedZones({ ...zones, isScanned: true })}
      />
      <AdvancedOrderflowMatrix 
        symbol={symbol}
        liveDataRef={liveDataRef}
        smartMoneyRef={smartMoneyRef}
      />
      <MultiTimeframeMatrix symbol={symbol} />
      <TimeBoxedOrderFlow defaultSymbol={symbol}/>
      <FundingRateHistory defaultSymbol={symbol}/>
      <DiffOrderBook symbol={symbol}/>
      <USStockOHLCMatrix />
      <USStockVisualAnalytics />
      
      {/* 1. Gắn Ref vào Simulator */}
      <LeverageSimulator 
        symbol={symbol} 
        ref={simulatorRef} 
      />
      
      {/* 2. Gắn Ref vào Radar */}
      <RealtimeDumpRadar 
        symbol={symbol} 
        macroZones={sharedZonesV3} 
        ref={radarRef} 
      />
      
      {/* 3. Gắn Ref vào V3 và nhận Tọa độ cho Radar */}
      <SupportResistanceRadarV3
        symbol={symbol} 
        liveDataRef={liveDataRef}
        smartMoneyRef={smartMoneyRef}
        ref={v3Ref}
        onZonesUpdate={(zones) => setSharedZonesV3(zones)}
      />
      
      <PatternMatrixXRay symbol={symbol} />
      
      <LiquidationSqueezeRadar 
        liveData={liveData} 
        smartMoneyRef={smartMoneyRef} 
        histDataList={histDataList} 
        spotDataList={spotDataList} 
        sharedZones={sharedZones} 
      />

      {/* 4. Gắn AI Council vào cuối & truyền hàm hút Data */}
      <AICouncilSection 
        symbol={symbol}
        onTakeSnapshot={buildSnapshotData} 
      />

    </div>
  );
}
